import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box, CssBaseline, Breadcrumbs, Link, Typography, Stack, Button,
    Paper, Grid, TextField, MenuItem, CircularProgress, Divider,
    Snackbar, Alert, useMediaQuery, useTheme
} from "@mui/material";
import {
    NavigateNext, ArrowBack, Save, Clear, Event, Badge
} from '@mui/icons-material';
import { Form, Formik } from "formik";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../component/header/Header";

const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    accent: '#FFC107',
    accentDark: '#FFA000',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037',
    white: '#FFFFFF',
    filterBg: "#FFF9E6"
};

const EditBatchScreen = () => {
    const { id } = useParams();
    const navigation = useNavigate();
    const formikRef = useRef();
    const isNonMobile = useMediaQuery("(min-width:700px)");

    const [batchDetail, setBatchDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // Generate Year List (Standardized)
    const yearList = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 10 }, (_, i) => {
            const yr = (currentYear + 1 - i).toString();
            return { id: yr, title: yr };
        });
    }, []);

    const statusList = [
        { id: "A", title: "Active" },
        { id: "I", title: "Inactive" }
    ];

    useEffect(() => {
        getBatchDetail();
        localStorage.setItem("current_tab", "Batch");
    }, [id]);

    const getBatchDetail = async () => {
        setLoading(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/batch/${id}`);
            const resJson = await response.json();
            if (resJson.status === 1) {
                setBatchDetail(resJson.data);
            } else {
                setSnackbar({ open: true, message: resJson.message, severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to connect to server", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (values) => {
        setLoadingSubmit(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/batch/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            });
            const resJson = await response.json();

            if (resJson.status === 1) {
                setSnackbar({ open: true, message: "Batch updated successfully!", severity: "success" });
                setTimeout(() => navigation("/Portal/Batch"), 1500);
            } else {
                setSnackbar({ open: true, message: resJson.message, severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Update failed", severity: "error" });
        } finally {
            setLoadingSubmit(false);
        }
    };

    const validationSchema = yup.object({
        batchName: yup.string().required("Batch Name is required"),
        batchYear: yup.string().required("Batch Year is required"),
        status: yup.string().required("Status is required"),
    });

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
            <CircularProgress sx={{ color: colors.accent }} />
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

                {/* Navigation & Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 1 }}>
                            <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                            <Link underline="hover" color="inherit" onClick={() => navigation("/Portal/Batch")} sx={{ cursor: 'pointer' }}>Batch</Link>
                            <Typography color="text.primary">Edit Batch</Typography>
                        </Breadcrumbs>
                        <Header title="Update Batch" subtitle={`Modifying details for ${batchDetail?.batchName}`} />
                    </Box>
                    <Button startIcon={<ArrowBack />} onClick={() => navigation("/Portal/Batch")} color="inherit">Back</Button>
                </Stack>

                <Formik
                    innerRef={formikRef}
                    enableReinitialize={true}
                    initialValues={{
                        batchName: batchDetail?.batchName || "",
                        batchYear: batchDetail?.batchYear || "",
                        status: batchDetail?.status || "A"
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleUpdate}
                >
                    {({ values, errors, touched, handleBlur, handleChange }) => (
                        <Form>
                            <Grid container spacing={3}>

                                {/* Batch Setup Segment */}
                                <Grid item xs={12} md={8}>
                                    <Paper elevation={0} sx={{ p: 3, bgcolor: colors.filterBg, borderRadius: 3, border: '1px solid #F0E6CC' }}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                                            <Badge sx={{ color: colors.textMain }} />
                                            <Typography variant="h6" fontWeight="bold">Batch Configuration</Typography>
                                        </Stack>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>Batch Name <span style={{ color: 'red' }}>*</span></Typography>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="batchName"
                                                    value={values.batchName}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.batchName && !!errors.batchName}
                                                    helperText={touched.batchName && errors.batchName}
                                                    sx={{ bgcolor: 'white' }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>Academic Year <span style={{ color: 'red' }}>*</span></Typography>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    name="batchYear"
                                                    value={values.batchYear}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.batchYear && !!errors.batchYear}
                                                    helperText={touched.batchYear && errors.batchYear}
                                                    sx={{ bgcolor: 'white' }}
                                                >
                                                    {yearList.map((val) => (
                                                        <MenuItem key={val.id} value={val.id}>{val.title}</MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>Current Status <span style={{ color: 'red' }}>*</span></Typography>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    name="status"
                                                    value={values.status}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.status && !!errors.status}
                                                    helperText={touched.status && errors.status}
                                                    sx={{ bgcolor: 'white' }}
                                                >
                                                    {statusList.map((val) => (
                                                        <MenuItem key={val.id} value={val.id}>{val.title}</MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Info Card Segment */}
                                <Grid item xs={12} md={4}>
                                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #F0E6CC', borderRadius: 3, height: '100%', bgcolor: '#FBFBFB' }}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                                            <Event sx={{ color: colors.accent }} />
                                            <Typography variant="h6" fontWeight="bold">Usage Tip</Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            Updating the academic year or name will affect all students assigned to this specific batch label. Please ensure the information is correct before saving.
                                        </Typography>
                                    </Paper>
                                </Grid>

                                {/* Submit Actions */}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                                        <Button variant="outlined" onClick={() => navigation("/Portal/Batch")} startIcon={<Clear />} sx={{ borderRadius: 2 }}>
                                            Discard Changes
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={loadingSubmit}
                                            startIcon={loadingSubmit ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                            sx={{
                                                bgcolor: colors.accent,
                                                color: 'black',
                                                px: 6,
                                                borderRadius: 3,
                                                fontWeight: 'bold',
                                                '&:hover': { bgcolor: colors.accentDark }
                                            }}
                                        >
                                            {loadingSubmit ? "Saving..." : "Update Batch"}
                                        </Button>
                                    </Stack>
                                </Grid>

                            </Grid>
                        </Form>
                    )}
                </Formik>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

            </Box>
        </Box>
    );
};

export default EditBatchScreen;